import React, { useMemo, useState, useEffect } from "react"

import { Container, CardBody, Card } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { useNavigate } from "react-router-dom"
import { axiosbaseurl } from "configure"
import moment from "moment"
import * as XLSX from "xlsx"

import { usePermissionData } from "AppContext"
import PermittedComponent from "components/Common/PermittedComponent"
import CommonTable from "components/Common/CommonTable"
import Loader from "components/Common/Loader"

const MissingACTRTaskReport = () => {
  const [table_data, settable_data] = useState([])
  const [loading, setloading] = useState(false)

  const getAllMissingRecords = async () => {
    setloading(true)
    try {
      const response = await axiosbaseurl.get(`/reports/missing/actr/tasks`)
      if (response.data.success) {
        settable_data(response.data.data)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setloading(false)
    }
  }

  useEffect(() => {
    getAllMissingRecords()
  }, [])

  const exportToExcel = () => {
    if (!table_data.length) return

    const exportData = table_data.map((item, index) => ({
      "#": index + 1,
      "Client Code": item.clientCode,
      "Client Name": item.clientName,
      "Accounts & Tax Current Year End": item.accountsAndTaxCurrentYearDueDate
        ? moment.utc(item.accountsAndTaxCurrentYearDueDate).format("DD/MM/YYYY")
        : "",
      "Accounts & Tax Next Year Due": item.accountsAndTaxNextYearDueDate
        ? moment.utc(item.accountsAndTaxNextYearDueDate).format("DD/MM/YYYY")
        : "",
      "Task Name": item.taskName,
      "Period End": item.startDate
        ? moment.utc(item.startDate).format("DD/MM/YYYY")
        : "N/A",
      "Period Due": item.dueDate
        ? moment.utc(item.dueDate).format("DD/MM/YYYY")
        : "N/A",
      "Task Created At": item.taskCreatedAt
        ? moment.utc(item.taskCreatedAt).format("DD/MM/YYYY")
        : "N/A",
      "Task Created By": item.taskCreatedBy,
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Missing ACTR Tasks")

    const maxWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(
        key.length,
        ...exportData.map(item => String(item[key]).length)
      ),
    }))
    worksheet["!cols"] = maxWidths

    XLSX.writeFileXLSX(
      workbook,
      `Missing_ACTR_Task_Report_${moment().format("DD_MM_YYYY_hh_mm_ss")}.xlsx`
    )
  }

  document.title = "ACTR Missing Task Report | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}
      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb
            title={"Dashboard"}
            breadcrumbItem={
              "Accounts and Corporation Tax Return Task Missing Report"
            }
          />
          <Card className="px-0 mx-0">
            <CardBody>
              <TableComponent
                table_data={table_data}
                exportToExcel={exportToExcel}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

const TableComponent = ({ table_data, exportToExcel }) => {
  const { checkAccess } = usePermissionData()
  const module_id = 24

  const navigate = useNavigate()

  // columns
  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        filterable: true,
        disableFilters: true,
        className: "text-center",
        Cell: cellProps => <span>{cellProps.row.index + 1}</span>,
      },
      {
        Header: "Client Code",
        accessor: "clientCode",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Client Name",
        accessor: "clientName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Accounts & Tax Return Current Year End (Per Companies House)",
        accessor: "accountsAndTaxCurrentYearDueDate",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => {
          const row = cellProps.row.original
          const currentYearDue = row?.accountsAndTaxCurrentYearDueDate
          const startDate = row?.startDate
          const isMatch =
            currentYearDue &&
            startDate &&
            moment.utc(currentYearDue).isSame(moment.utc(startDate), "day")
          const isMismatch =
            (currentYearDue && !startDate) ||
            (!currentYearDue && startDate) ||
            (currentYearDue && startDate && !isMatch)
          const color = isMismatch ? "#f15757" : isMatch ? "#25c24d" : "inherit"
          return (
            <span style={{ color }}>
              {currentYearDue
                ? moment.utc(currentYearDue).format("DD/MM/YYYY")
                : ""}
            </span>
          )
        },
      },
      {
        Header:
          "Accounts & Tax Return Current Financial/Tax Year Due (Per Companies House)",
        accessor: "accountsAndTaxNextYearDueDate",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => {
          const row = cellProps.row.original
          const nextYearDue = row?.accountsAndTaxNextYearDueDate
          const dueDate = row?.dueDate
          const isMatch =
            nextYearDue &&
            dueDate &&
            moment.utc(nextYearDue).isSame(moment.utc(dueDate), "day")
          const isMismatch =
            (nextYearDue && !dueDate) ||
            (!nextYearDue && dueDate) ||
            (nextYearDue && dueDate && !isMatch)
          const color = isMismatch ? "#f15757" : isMatch ? "#25c24d" : "inherit"
          return (
            <span style={{ color }}>
              {nextYearDue ? moment.utc(nextYearDue).format("DD/MM/YYYY") : ""}
            </span>
          )
        },
      },
      {
        Header: "Task Name",
        accessor: "taskName",
        disableFilters: true,
        filterable: true,
      },
      {
        Header: "Period End",
        accessor: "startDate",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => {
          const row = cellProps.row.original
          const startDate = row?.startDate
          const currentYearDue = row?.accountsAndTaxCurrentYearDueDate
          const isMatch =
            startDate &&
            currentYearDue &&
            moment.utc(startDate).isSame(moment.utc(currentYearDue), "day")
          const isMismatch =
            (startDate && !currentYearDue) ||
            (!startDate && currentYearDue) ||
            (startDate && currentYearDue && !isMatch)
          const color = isMismatch ? "#f15757" : isMatch ? "#25c24d" : "inherit"
          return (
            <span style={{ color }}>
              {startDate ? moment.utc(startDate).format("DD/MM/YYYY") : "N/A"}
            </span>
          )
        },
      },
      {
        Header: "Period Due",
        accessor: "dueDate",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => {
          const row = cellProps.row.original
          const dueDate = row?.dueDate
          const nextYearDue = row?.accountsAndTaxNextYearDueDate
          const isMatch =
            dueDate &&
            nextYearDue &&
            moment.utc(dueDate).isSame(moment.utc(nextYearDue), "day")
          const isMismatch =
            (dueDate && !nextYearDue) ||
            (!dueDate && nextYearDue) ||
            (dueDate && nextYearDue && !isMatch)
          const color = isMismatch ? "#f15757" : isMatch ? "#25c24d" : "inherit"
          return (
            <span style={{ color }}>
              {dueDate ? moment.utc(dueDate).format("DD/MM/YYYY") : "N/A"}
            </span>
          )
        },
      },
      {
        Header: "Task Created At",
        accessor: "taskCreatedAt",
        disableFilters: true,
        filterable: true,
        Cell: cellProps => {
          return (
            <span>
              {cellProps.row.original?.taskCreatedAt
                ? moment
                    .utc(cellProps.row.original?.taskCreatedAt)
                    .format("DD/MM/YYYY")
                : "N/A"}
            </span>
          )
        },
      },
      {
        Header: "Task Created By",
        accessor: "taskCreatedBy",
        disableFilters: true,
        filterable: true,
      },
      // {
      //   Header: "Status",
      //   accessor: "status",
      //   disableFilters: true,
      //   filterable: true,
      // },
    ],
    []
  )

  // table data
  const data = useMemo(() => table_data, [table_data])

  return (
    <>
      <PermittedComponent isAccess={checkAccess(module_id, "view")}>
        <CommonTable
          columns={columns}
          data={data}
          heading={
            <>
              <h5 className="fw-bold text-primary">
                Accounts and Corporation Tax Return Task Missing Report
              </h5>
              <h6 className="fw-medium">
                Cross-reference Limited Company client dates with Accounts and
                Corporation Tax Return tasks
              </h6>
            </>
          }
        >
          <div className="d-flex">
            <div>
              <button
                onClick={exportToExcel}
                className="btn btn-primary bg-primary ms-3"
              >
                <i className="fas fa-download me-3"></i>
                Export
              </button>
            </div>
            <div>
              <button
                onClick={() => navigate(-1)}
                className="btn btn-secondary bg-dark ms-3"
              >
                <i className="fas fa-arrow-left me-3"></i>
                Back
              </button>
            </div>
          </div>
        </CommonTable>
      </PermittedComponent>
    </>
  )
}

export default MissingACTRTaskReport
